import React, { FunctionComponent } from "react";
import styled from "styled-components";

import * as T from "../Typography";
import * as I from "../Icons";
import * as U from "../Utils";
import * as u from "../../utils";

type StyleProps = {
  open?: boolean;
  maxMobileHeight?: string;
  maxHeight?: string;
  noGap?: boolean;
};

type DropdownRowProps = {
  title: string;
  children: React.ReactNode;
  noGap?: boolean;
  dataCy?: string;
  defaultOpen?: boolean;
  autoClose?: boolean;
  colour?: string;
  divider?: boolean;
  maxHeight?: string;
  maxMobileHeight?: string;
  line?: boolean;
};

const DropdownRow: FunctionComponent<DropdownRowProps> = (props) => {
  const innerRef = React.useRef(null);
  const [open, setOpen] = React.useState(props.defaultOpen || false);

  const attachOutsideClick = () => {
    if (!props.autoClose) return null;
    document.addEventListener("click", onMaybeOutside);
    return () => document.removeEventListener("click", onMaybeOutside);

    function onMaybeOutside(evt) {
      if (innerRef.current?.contains(evt.target)) return;
      setOpen(false);
    }
  };

  React.useEffect(attachOutsideClick, []);

  return (
    <S.Container data-cy={props.dataCy} ref={innerRef}>
      <S.TitleContainer
        onClick={() => setOpen(!open)}
        noGap={props.noGap}
        data-cy={`${props.dataCy}-toggle`}
      >
        <T.CM weight="600" colour={props?.colour}>
          {props.title}
        </T.CM>
        <S.IconWrap open={open}>
          <I.ArrowDown colour={props?.colour} />
        </S.IconWrap>
      </S.TitleContainer>
      <S.HiddenContainer open={open} {...props}>
        {props.children}
      </S.HiddenContainer>
      {props.line && (
        <>
          <U.Space height={5} />
          <U.Divider borderColour={props.colour} borderSize={2} width={25} />
        </>
      )}
      {props.divider && (
        <>
          <U.Space height={5} />
          <U.Divider borderColour={u.colours.shellOverlay} />
        </>
      )}
    </S.Container>
  );
};

const S = () => {};

S.Container = styled.div``;

S.TitleContainer = styled.div<StyleProps>`
  padding: 0 5px 15px 0;
  display: flex;
  align-items: center;
  justify-content: ${(p) => !p.noGap && "space-between"};
  gap: 10px;
  cursor: pointer;
`;

S.IconWrap = styled.div<StyleProps>`
  transition: transform 0.2s linear;
  transform: ${(p) => (p.open ? "rotate(0)" : "rotate(-90deg)")};
`;

S.HiddenContainer = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  overflow-x: visible;
  overflow-y: hidden;
  transition: max-height 0.2s linear;

  ${(p) =>
    p.maxMobileHeight
      ? `max-height: ${p.open ? p.maxMobileHeight : 0}px;`
      : `max-height: ${p.open ? p.maxHeight : 0}px;`}

  @media ${u.device.mobile} {
    max-height: ${(p) => (p.open ? p.maxHeight : 0)}px;
  }
`;

export default DropdownRow;
